import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { cn } from '@/lib/utils';
import { imgProps } from '@/lib/img';

const ease = [0.22, 1, 0.36, 1];

/** Fade + rise entrance on scroll into view. */
export const Reveal = ({ children, className, delay = 0, y = 24, once = true, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: '-10% 0px' }}
      transition={{ duration: 0.9, ease, delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/** Image that drifts slowly as the user scrolls (subtle parallax). */
export const ParallaxImage = ({ src, alt = '', className, imgClassName, strength = 40, ratio = 'aspect-[4/5]' }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  return (
    <div ref={ref} className={cn('relative overflow-hidden rounded-xl bg-muted', ratio, className)}>
      <motion.img
        {...imgProps(src, { sizes: '(min-width: 768px) 60vw, 100vw' })}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={reduce ? undefined : { y, scale: 1.12 }}
        className={cn('absolute inset-0 h-full w-full object-cover', imgClassName)}
      />
    </div>
  );
};

/** Full-bleed banner with a fixed-feel parallax background. */
export const ParallaxBanner = ({ src, children, className, height = 'min-h-[60vh]' }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);
  return (
    <section ref={ref} className={cn('relative overflow-hidden flex items-center justify-center text-white', height, className)}>
      <motion.div style={reduce ? undefined : { y }} className="absolute inset-[-14%_0]">
        <img {...imgProps(src, { sizes: '100vw' })} alt="" role="presentation" className="h-full w-full object-cover" loading="lazy" decoding="async" />
      </motion.div>
      <div className="absolute inset-0 bg-[rgba(17,24,39,0.45)]" />
      <div className="relative z-10 container-x w-full">{children}</div>
    </section>
  );
};

export const SectionHeading = ({ label, title, body, align = 'left', dark = false, className }) => (
  <div className={cn(align === 'center' ? 'text-center mx-auto' : '', 'max-w-2xl', className)}>
    {label && (
      <Reveal>
        <p className={cn('eyebrow mb-4', dark ? 'text-white/70' : 'text-[hsl(var(--brand-teal))]')}>{label}</p>
      </Reveal>
    )}
    {title && (
      <Reveal delay={0.05}>
        <h2 className={cn('font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] tracking-tight', dark ? 'text-white' : 'text-foreground')}>{title}</h2>
      </Reveal>
    )}
    {body && (
      <Reveal delay={0.1}>
        <p className={cn('mt-6 text-[15px] sm:text-base leading-[1.85]', dark ? 'text-white/80' : 'text-foreground/75')}>{body}</p>
      </Reveal>
    )}
  </div>
);
